import { useEffect, useState } from 'react';
import { ScreenBackdrop } from './ScreenBackdrop';
import { Numpad, type NumpadKey } from './Numpad';
import { CloseIcon } from './icons';
import { useBackButton, hasTelegramBackButton } from '../../hooks/useBackButton';

type Props = {
  title: string;
  onComplete: (pin: string) => void;
  onClose: () => void;
  /** Lock mode — no back/close button (the screen can't be dismissed). */
  lock?: boolean;
  /** Render the title in red (e.g. after a wrong PIN). */
  error?: boolean;
};

export function PinScreen({ title, onComplete, onClose, lock = false, error = false }: Props) {
  const [pin, setPin] = useState('');
  // Set once the 4th digit lands — the parent's async work (reauth / save) can
  // take a couple of seconds, so we show a spinner instead of a blank wait.
  const [submitting, setSubmitting] = useState(false);
  useBackButton(onClose, !lock);
  const tgBack = hasTelegramBackButton();

  // Auto-submit once the 4th digit lands. Done in an effect (not inside the
  // setPin updater) so it fires exactly once — an updater with side effects
  // double-fires under React StrictMode, which double-submitted the PIN.
  useEffect(() => {
    if (pin.length !== 4 || submitting) return;
    const id = setTimeout(() => {
      setSubmitting(true);
      onComplete(pin);
    }, 180);
    return () => clearTimeout(id);
  }, [pin, submitting, onComplete]);

  const handleKey = (k: NumpadKey) => {
    if (submitting) return;
    if (k === 'back') {
      setPin((p) => p.slice(0, -1));
      return;
    }
    if (k === '.') return;
    setPin((p) => (p.length >= 4 ? p : p + k));
  };

  return (
    <div className="relative flex h-full min-h-full flex-col">
      <ScreenBackdrop full />
      <div className="relative z-10 flex h-full min-h-full flex-col px-4 pb-[max(1.25rem,var(--tg-safe-bottom))] pt-[calc(var(--tg-safe-top)_+_0.75rem)]">
        {!tgBack && !lock && (
          <div className="flex h-10 items-center">
            <button
              onClick={onClose}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-black/[0.35] text-white"
              aria-label="Close"
            >
              <CloseIcon size={16} />
            </button>
          </div>
        )}
        <h1 className={`mt-6 text-center text-[16px] font-medium ${error ? 'text-[#ff3b30]' : 'text-ink'}`}>
          {title}
        </h1>

        {/* PIN boxes — vertically centered between header and keypad */}
        <div className="flex flex-1 flex-col items-center justify-center gap-6">
          <div className="flex justify-center gap-3">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className={`flex h-16 w-16 items-center justify-center overflow-hidden rounded-[18px] bg-white text-[24px] font-medium tabular-nums text-ink transition-shadow ${
                  i === pin.length && !submitting ? 'ring-2 ring-ink' : ''
                }`}
              >
                {pin[i] != null && (
                  <span key={pin[i]} className="pin-pop">
                    {pin[i]}
                  </span>
                )}
              </div>
            ))}
          </div>
          <span
            className={`h-6 w-6 animate-spin rounded-full border-2 border-ink/15 border-t-ink transition-opacity ${
              submitting ? 'opacity-100' : 'opacity-0'
            }`}
          />
        </div>

        <div className={submitting ? 'pointer-events-none opacity-40 transition-opacity' : 'transition-opacity'}>
          <Numpad onKey={handleKey} decimal={false} />
        </div>
      </div>
    </div>
  );
}
